import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, CircleDashed, HelpCircle, PauseCircle, XCircle } from 'lucide-react';
import { StatusChip } from '@nyce/admin-kit/console';

// Status vocabulary shared by the dashboard and the discovery lists: ok | warn | down | error | stopped | idle | unknown.

export type Tone = 'green' | 'amber' | 'red' | 'grey';
const STATUS: Record<string, { label: string; tone: Tone; icon: ReactNode; rank: number }> = {
  down: { label: 'Down', tone: 'red', icon: <XCircle size={14} />, rank: 0 },
  error: { label: 'Unreachable', tone: 'red', icon: <HelpCircle size={14} />, rank: 1 },
  warn: { label: 'Degraded', tone: 'amber', icon: <AlertTriangle size={14} />, rank: 2 },
  unknown: { label: 'Unknown', tone: 'grey', icon: <HelpCircle size={14} />, rank: 3 },
  stopped: { label: 'Stopped', tone: 'grey', icon: <PauseCircle size={14} />, rank: 4 },
  ok: { label: 'Healthy', tone: 'green', icon: <CheckCircle2 size={14} />, rank: 5 },
  idle: { label: 'Scaled to 0', tone: 'grey', icon: <CircleDashed size={14} />, rank: 6 },
};
const meta = (s: string) => STATUS[s] ?? STATUS.unknown;
export const bySeverity = <T extends { status: string; name: string }>(a: T, b: T) =>
  meta(a.status).rank - meta(b.status).rank || a.name.localeCompare(b.name);

/** Status with icon + label (never colour alone). */
export function StatusBadge({ status }: { status: string }) {
  const m = meta(status);
  return <StatusChip value={status} tone={m.tone} label={m.label} icon={m.icon} className="oph-status" />;
}

/** The worst of several statuses (ok when there are none). */
export const worstStatus = (statuses: string[]): string =>
  statuses.reduce((w, s) => (meta(s).rank < meta(w).rank ? s : w), 'ok');
